<template>
  <div class="led-selector">
    <h2>INDICATOR COLOR</h2>
    <div class="header-divider"></div>
    <div class="led-grid">
      <div
        v-for="led in leds"
        :key="led.id"
        class="led-item"
      >
        <div
          @click="selectLED(led.id)"
          :class="['led-preview', { active: currentLED === led.id }]"
          :style="getLEDStyle(led.id)"
          :aria-label="led.displayName"
        ></div>
        <div class="led-label">
          {{ led.displayName }}
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { LED_MATERIALS } from '@/config/ledConfig';

export default defineComponent({
  name: 'LEDSelector',
  props: {
    currentLED: {
      type: String,
      required: true
    }
  },
  emits: ['ledChanged'],
  setup(props, { emit }) {
    const leds = LED_MATERIALS;

    const selectLED = (ledId: string) => {
      emit('ledChanged', ledId);
    };

    const LED_STYLES: { [key: string]: any } = {
      'led_red': {
        backgroundImage: 'url(/buttons/whiteledd3.png)',
        backgroundSize: '105%',
        backgroundPosition: '70% 35%',
        backgroundRepeat: 'no-repeat',
        filter: 'brightness(1.1) contrast(1.1)',
        boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.2)'
      },
      'led_blue': {
        backgroundImage: 'url(/buttons/pinkledd2.png)',
        backgroundSize: '107%',
        backgroundPosition: '55% 60%',
        backgroundRepeat: 'no-repeat',
        filter: 'brightness(1.1) contrast(1.1)',
        boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.2)'
      },
      'led_green': {
        backgroundImage: 'url(/buttons/blueledd3.png)',
        backgroundSize: '101%',
        backgroundPosition: '52% 50%',
        backgroundRepeat: 'no-repeat',
        filter: 'brightness(1.1) contrast(1.1)',
        boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.2)'
      },
      'led_white': {
        backgroundImage: 'url(/buttons/purpleledd2.png)',
        backgroundSize: '103%',
        backgroundPosition: '90% 50%',
        backgroundRepeat: 'no-repeat',
        filter: 'brightness(1.1) contrast(1.1)',
        boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.2)'
      }
    };

    const getLEDStyle = (ledId: string) => {
      return LED_STYLES[ledId] || {};
    };

    return {
      leds,
      selectLED,
      getLEDStyle
    };
  }
});
</script>

<style scoped>
.led-selector {
  margin-bottom: 5.442rem;
}

.header-divider {
  width: 100%;
  height: 1px;
  background: rgba(0, 0, 0, 0.35);
  margin: 0.35rem 0 2.5515rem 0;
}

h2 {
  font-size: 2.6833rem; /* 2.4394rem * 1.10 */
  margin-bottom: 0.2rem;
  margin-left: .2rem;
  color: rgb(0, 0, 0);
  font-weight: 400;
  letter-spacing: 0.000em;
}

.led-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, 95px);
  /* Match Enclosure button horizontal spacing */
  column-gap: 1.56rem;
  row-gap: 1.2rem;
  align-items: start;
}

.led-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.638rem; /* 1.365rem * 1.20 */
}

.led-preview {
  width: 95px;
  height: 95px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  /* Let the active ring render outside the circle */
  overflow: visible;
  /* Ring tuning */
  --active-ring-offset: 8.5px;
  --active-ring-width: 3px;
}

.led-preview.active::after {
  content: '';
  position: absolute;
  left: calc(-1 * var(--active-ring-offset));
  top: calc(-1 * var(--active-ring-offset));
  right: calc(-1 * var(--active-ring-offset));
  bottom: calc(-1 * var(--active-ring-offset));
  border: var(--active-ring-width) solid rgb(0, 0, 0);
  border-radius: 50%;
  pointer-events: none;
}

.led-label {
  font-family: "Cormorant Garamond", "Times New Roman", Times, serif;
  font-weight: 300;
  font-size: 1.5273rem; /* 1.3281rem * 1.15 */
  line-height: 1.05;
  color: rgb(0, 0, 0);
  text-align: center;
  letter-spacing: 0.005em;
  max-width: 90px;
}

/* Mobile responsive */
@media screen and (max-width: 768px) {
  .led-selector {
    margin-bottom: calc(5.442rem - 26px);
  }

  .header-divider {
    margin: 0.35rem 0 2.0412rem 0; /* 2.5515rem * 0.80 */
  }

  .led-grid {
    grid-template-columns: repeat(4, 1fr);
    /* Match Enclosure button horizontal spacing */
    column-gap: 1.4458rem;
    row-gap: 1.452rem;
  }

  .led-item {
    gap: 0.9804rem;
  }

  .led-label {
    font-size: 1.2982rem;
  }
  
  .led-preview {
    width: 70px;
    height: 70px;
  }
  
  h2 {
    font-size: 2.3232rem;
  }
}
</style> 